import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { DashboardToday, getDashboardToday } from "../../api/dashboardService";
import { REFRESH_MS } from "./dashboardTodayUtils";

export interface TodayState {
  data: DashboardToday | null;
  loading: boolean;
  error: boolean;
  refresh: () => void;
}

/**
 * Loads the Today payload, then refetches silently every minute and when the
 * tab regains focus. A failed refresh keeps the last numbers on screen and
 * toasts once until a refresh succeeds again.
 */
export function useDashboardToday(): TodayState {
  const [data, setData] = useState<DashboardToday | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const requestId = useRef(0);
  const toastedFailure = useRef(false);

  const load = useCallback(() => {
    const id = ++requestId.current;
    getDashboardToday()
      .then((next) => {
        if (id !== requestId.current) return;
        setData(next);
        setError(false);
        toastedFailure.current = false;
      })
      .catch(() => {
        if (id !== requestId.current) return;
        setError(true);
        if (!toastedFailure.current) {
          toast.error("Could not refresh the dashboard");
          toastedFailure.current = true;
        }
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  return { data, loading, error, refresh: load };
}
